import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { FlaskConical, ImageIcon } from 'lucide-react';
import Sidebar from '../../components/sidebar/Sidebar';
import ThrottledImage from '../../components/common/ThrottledImage';
import {
  ResponsiveAppShell,
  TaskyBadge,
  TaskyButton,
  TaskyDialog,
  TaskyEmptyState,
  TaskyErrorState,
  TaskySpinner,
  TaskyTable,
  TaskyTableBody,
  TaskyTableCell,
  TaskyTableHead,
  TaskyTableHeaderCell,
  TaskyTableRow,
  TaskyTopBar,
  TaskyPageContainer,
} from '../../components/tasky';
import { listBannerAbTests } from '../../services/api/bannerAbTestService';
import {
  BANNER_AB_DISCLAIMER,
  BANNER_AB_MODE_LABELS,
  BANNER_AB_STATUS_LABELS,
  BANNER_AB_STATUS_VARIANTS,
  BANNER_AB_STOPPED_REASON_LABELS,
  FACTOR_LABELS,
  type BannerAbImageSummary,
  type BannerAbStoppedReason,
  type BannerAbTest,
  type Paginated,
} from '../../types/bannerAbTest';

/** 作成日は分まで。秒まで出すと列が広がるわりに読み取る情報が無い */
const CREATED_AT_FORMAT: Intl.DateTimeFormatOptions = {
  year: 'numeric', month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit',
};

/** TaskyButton variant="outline" size="sm" 相当。Link に被せて見た目だけ揃える */
const LINK_BUTTON_CLASS =
  'inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium transition-colors ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ' +
  '[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 ' +
  'bg-gray-200 text-dark hover:bg-gray-300 rounded-tasky-sm h-9 px-3 text-sm';

/** 一覧セルの 48px 角サムネ。ThrottledImage は wrapper と img にクラスを分けて渡す */
const CELL_THUMB_WRAPPER = 'h-12 w-12 shrink-0 overflow-hidden rounded border border-warm-border bg-gray-100';
const CELL_THUMB_IMG = 'h-full w-full object-cover';

/** 一覧に出すサムネ1枚。サムネ URL が無ければ原寸にフォールバックする */
const CellThumb: React.FC<{ image: BannerAbImageSummary; alt: string }> = ({ image, alt }) => (
  <ThrottledImage
    src={image.thumbnail_url || image.url}
    alt={alt}
    wrapperClassName={CELL_THUMB_WRAPPER}
    imgClassName={CELL_THUMB_IMG}
  />
);

/** 全候補を並べるモーダル。勝者にはバッジを重ねる */
const CandidatesDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  testId: number;
  candidates: BannerAbImageSummary[];
  winnerId?: number | null;
}> = ({ open, onClose, testId, candidates, winnerId }) => (
  <TaskyDialog
    open={open}
    onClose={onClose}
    title={`候補バナー（テスト #${testId}）`}
    size="xl"
    footer={<TaskyButton type="button" variant="outline" size="sm" onClick={onClose}>閉じる</TaskyButton>}
  >
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
      {candidates.map((image) => (
        <div key={image.id} className="relative">
          <ThrottledImage
            src={image.thumbnail_url || image.url}
            alt={`候補 #${image.id}`}
            wrapperClassName="w-full overflow-hidden rounded border border-warm-border bg-gray-100"
            imgClassName="block h-auto w-full"
          />
          {winnerId === image.id && (
            <TaskyBadge variant="success" className="absolute left-1 top-1">推奨</TaskyBadge>
          )}
        </div>
      ))}
    </div>
  </TaskyDialog>
);

/** 選ばれた理由。1件だけ出して、残りは「続きを見る」で展開する */
const ReasonsCell: React.FC<{ reasons: string[] }> = ({ reasons }) => {
  const [expanded, setExpanded] = useState(false);

  if (reasons.length === 0) return <>-</>;

  return (
    <div className="max-w-xs space-y-1">
      <p className="text-sm text-dark">{reasons[0]}</p>
      {reasons.length > 1 && (
        <>
          {expanded && (
            <ul className="list-disc space-y-0.5 pl-4 text-sm text-dark">
              {/* API 側で重複除去しているが、同じ文言が来ても duplicate key にならないよう index を混ぜる */}
              {reasons.slice(1).map((reason, index) => (
                <li key={`${index}-${reason}`}>{reason}</li>
              ))}
            </ul>
          )}
          <button type="button" className="whitespace-nowrap text-xs text-primary hover:underline" onClick={() => setExpanded((v) => !v)}>
            {expanded ? '閉じる' : '続きを見る'}
          </button>
        </>
      )}
    </div>
  );
};

/** 一覧の1行。候補モーダルの開閉は行ごとに持つ */
const BannerAbTestListRow: React.FC<{ test: BannerAbTest }> = ({ test }) => {
  const [candidatesOpen, setCandidatesOpen] = useState(false);
  const candidates = test.candidates ?? [];
  const products = test.products ?? [];
  const hiddenCount = Math.max(0, candidates.length - 2);

  return (
    <>
      <TaskyTableRow>
        <TaskyTableCell className="whitespace-nowrap">
          <Link to={`/ads/banner-ab-tests/${test.id}`} className="font-medium text-primary hover:underline">#{test.id}</Link>
        </TaskyTableCell>
        <TaskyTableCell>
          <div className="space-y-0.5">
            <TaskyBadge variant={test.mode === 'auto_loop' ? 'info' : 'secondary'}>
              {BANNER_AB_MODE_LABELS[test.mode]}
            </TaskyBadge>
            <div className="whitespace-nowrap text-xs text-gray-500">
              {test.mode === 'auto_loop'
                ? `${test.current_round ?? 0} / ${test.max_rounds ?? 0} ラウンド`
                : `${test.candidate_image_ids.length} 枚`}
            </div>
          </div>
        </TaskyTableCell>
        <TaskyTableCell className="whitespace-nowrap text-gray-500">
          {Number.isNaN(Date.parse(test.created_at))
            ? '-'
            : new Date(test.created_at).toLocaleString('ja-JP', CREATED_AT_FORMAT)}
        </TaskyTableCell>
        <TaskyTableCell>
          {candidates.length === 0 ? '-' : (
            <div className="flex items-center gap-2">
              {candidates.slice(0, 2).map((image) => (
                <CellThumb key={image.id} image={image} alt={`候補 #${image.id}`} />
              ))}
              {hiddenCount > 0 && (
                <TaskyButton type="button" variant="outline" size="sm" onClick={() => setCandidatesOpen(true)}>
                  +{hiddenCount}
                </TaskyButton>
              )}
            </div>
          )}
        </TaskyTableCell>
        <TaskyTableCell>
          {test.winner ? <CellThumb image={test.winner} alt={`推奨 #${test.winner.id}`} /> : '-'}
        </TaskyTableCell>
        <TaskyTableCell className="whitespace-nowrap text-sm text-dark">
          {/* 0% を「-」に落とさないよう null と 0 を区別する */}
          {test.winner_preference == null ? '-' : `${Math.round(test.winner_preference * 100)}%`}
        </TaskyTableCell>
        <TaskyTableCell className="whitespace-nowrap text-sm text-dark">
          {test.winner_factor ? FACTOR_LABELS[test.winner_factor] : '-'}
        </TaskyTableCell>
        {/* 表が枠より広いと各列が最小幅まで縮むので、商品名が数文字ずつに割れない幅を残す */}
        <TaskyTableCell className="min-w-[8rem]">
          {products.length === 0 ? '-' : products.map((product, i) => (
            <React.Fragment key={product.id}>
              {i > 0 && '、'}
              <Link to={`/knowledge?product=${product.id}`} className="text-primary hover:underline text-sm">
                {product.name}
              </Link>
            </React.Fragment>
          ))}
        </TaskyTableCell>
        <TaskyTableCell>
          <div className="space-y-0.5">
            <TaskyBadge variant={BANNER_AB_STATUS_VARIANTS[test.status]}>{BANNER_AB_STATUS_LABELS[test.status]}</TaskyBadge>
            {test.stopped_reason && BANNER_AB_STOPPED_REASON_LABELS[test.stopped_reason as BannerAbStoppedReason] && (
              <div className="max-w-[14rem] text-xs text-gray-500">
                {BANNER_AB_STOPPED_REASON_LABELS[test.stopped_reason as BannerAbStoppedReason]}
              </div>
            )}
          </div>
        </TaskyTableCell>
        <TaskyTableCell>
          <ReasonsCell reasons={test.winner_reasons ?? []} />
        </TaskyTableCell>
      </TaskyTableRow>
      <CandidatesDialog
        open={candidatesOpen}
        onClose={() => setCandidatesOpen(false)}
        testId={test.id}
        candidates={candidates}
        winnerId={test.winner?.id}
      />
    </>
  );
};

/** 過去のオートバナーABテスト一覧。開始はバナーの詳細の「オートABテスト」から（一覧で選んで始める入口は止めている） */
const BannerAbTestListPage: React.FC = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  // 表示・活性・遷移はすべて currentPage が正。API が返す current_page は見ない
  const [currentPage, setCurrentPage] = useState(1);
  const [page, setPage] = useState<Paginated<BannerAbTest> | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // 最後に取得できたページ。取得に失敗したらここへ巻き戻す（ページ番号が先へ進み続けるのを防ぐ）
  const loadedPageRef = useRef<number | null>(null);

  useEffect(() => {
    // 失敗の巻き戻しで戻ってきた＝いま表示しているページなので、取り直さずエラー表示を残す
    if (loadedPageRef.current === currentPage) return;

    let active = true;
    setIsLoading(true);
    setError(null);
    listBannerAbTests(currentPage)
      .then((p) => {
        if (!active) return;
        setPage(p);
        loadedPageRef.current = currentPage;
        setIsLoading(false);
      })
      .catch(() => {
        if (!active) return;
        setError('一覧の読み込みに失敗しました');
        setIsLoading(false);
        if (loadedPageRef.current !== null) setCurrentPage(loadedPageRef.current);
      });
    return () => { active = false; };
  }, [currentPage]);

  const rows = page?.data ?? [];

  return (
    <ResponsiveAppShell
      sidebar={<Sidebar collapsed={sidebarCollapsed} onToggleCollapse={setSidebarCollapsed} />}
      mobileSidebar={<Sidebar collapsed={false} onToggleCollapse={() => undefined} />}
      topbarTitle="オートバナーABテスト"
    >
      <div className="flex min-h-[calc(100dvh-56px)] flex-col overflow-hidden bg-white lg:h-screen lg:min-h-0">
        {/* Header */}
        <TaskyTopBar
          title="オートバナーABテスト"
          icon={<FlaskConical className="w-5 h-5" />}
          sidebarCollapsed={sidebarCollapsed}
          onExpandSidebar={() => setSidebarCollapsed(false)}
        >
          <Link to="/ads/banner" className={LINK_BUTTON_CLASS}>
            <ImageIcon className="w-4 h-4" />
            バナー広告へ
          </Link>
        </TaskyTopBar>

        {/* Main Scrollable Content */}
        <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden">
          <TaskyPageContainer className="space-y-4">
            <p data-tutorial="ads-banner-ab-tests-intro" className="text-sm text-gray-500">
              バナーの詳細から「オートABテスト」で自動改善ループを開始できます。
            </p>

            {error && <TaskyErrorState title={error} />}

            {isLoading && !page && (
              <div className="flex justify-center py-12">
                <TaskySpinner size="lg" />
              </div>
            )}

            {page && rows.length === 0 && (
              <TaskyEmptyState
                icon={<FlaskConical />}
                title="まだABテストはありません"
              />
            )}

            {rows.length > 0 && (
              /* TaskyTable のラッパが overflow-x-auto なので、狭い画面では横スクロールになる */
              <TaskyTable data-tutorial="ads-banner-ab-tests-table" className="min-w-[980px]">
                <TaskyTableHead>
                  <tr>
                    <TaskyTableHeaderCell>ID</TaskyTableHeaderCell>
                    <TaskyTableHeaderCell>種別</TaskyTableHeaderCell>
                    <TaskyTableHeaderCell>作成日</TaskyTableHeaderCell>
                    <TaskyTableHeaderCell>候補</TaskyTableHeaderCell>
                    <TaskyTableHeaderCell>推奨</TaskyTableHeaderCell>
                    <TaskyTableHeaderCell>選ばれた割合</TaskyTableHeaderCell>
                    <TaskyTableHeaderCell>決め手</TaskyTableHeaderCell>
                    <TaskyTableHeaderCell>商品</TaskyTableHeaderCell>
                    <TaskyTableHeaderCell>状態</TaskyTableHeaderCell>
                    <TaskyTableHeaderCell>理由</TaskyTableHeaderCell>
                  </tr>
                </TaskyTableHead>
                <TaskyTableBody>
                  {rows.map((t) => (
                    <BannerAbTestListRow key={t.id} test={t} />
                  ))}
                </TaskyTableBody>
              </TaskyTable>
            )}

            {page && page.last_page > 1 && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">{currentPage} / {page.last_page} ページ（全 {page.total} 件）</span>
                <div className="flex items-center gap-2">
                  <TaskyButton
                    variant="outline"
                    size="sm"
                    disabled={isLoading || currentPage <= 1}
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  >
                    前へ
                  </TaskyButton>
                  <TaskyButton
                    variant="outline"
                    size="sm"
                    disabled={isLoading || currentPage >= page.last_page}
                    onClick={() => setCurrentPage((p) => p + 1)}
                  >
                    次へ
                  </TaskyButton>
                </div>
              </div>
            )}

            <p className="text-xs text-gray-500">{BANNER_AB_DISCLAIMER}</p>
          </TaskyPageContainer>
        </div>
      </div>
    </ResponsiveAppShell>
  );
};

export default BannerAbTestListPage;
